"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { useTheme } from "next-themes";

type Gpu = {
  name: string;
  rps: number;
};

type ThemeColor = {
  css: string;
  rgb: [number, number, number];
};

type ThemePalette = {
  accent: ThemeColor;
  background: ThemeColor;
  foreground: ThemeColor;
  isDark: boolean;
  mutedForeground: ThemeColor;
};

type Dot = {
  alpha: number;
  size: number;
  speed: number;
  x: number;
  yOff: number;
};

type LaneState = {
  accum: number;
  dots: Dot[];
};

const GPUS: readonly Gpu[] = [
  { name: "H100", rps: 62.83 },
  { name: "A100", rps: 21.36 },
  { name: "L40S", rps: 21.34 },
  { name: "A10", rps: 9.0 },
  { name: "L4", rps: 7.61 },
  { name: "Jetson", rps: 3.66 },
] as const;

const MAX_RPS = Math.max(...GPUS.map((g) => g.rps));
const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];
const STAGGER = 0.2;

const FALLBACK_PALETTES: Record<"dark" | "light", ThemePalette> = {
  dark: {
    accent: { css: "rgb(85, 180, 214)", rgb: [85, 180, 214] },
    background: { css: "rgb(36, 35, 41)", rgb: [36, 35, 41] },
    foreground: { css: "rgb(250, 250, 250)", rgb: [250, 250, 250] },
    isDark: true,
    mutedForeground: { css: "rgb(165, 163, 175)", rgb: [165, 163, 175] },
  },
  light: {
    accent: { css: "rgb(74, 174, 211)", rgb: [74, 174, 211] },
    background: { css: "rgb(250, 249, 246)", rgb: [250, 249, 246] },
    foreground: { css: "rgb(26, 24, 24)", rgb: [26, 24, 24] },
    isDark: false,
    mutedForeground: { css: "rgb(118, 113, 113)", rgb: [118, 113, 113] },
  },
};

function resolveColor(
  probe: HTMLDivElement,
  variable: string,
  fallback: ThemeColor
): ThemeColor {
  probe.style.color = `var(${variable}, ${fallback.css})`;
  const resolved = window.getComputedStyle(probe).color;
  const parts = resolved.match(/[\d.]+/g);
  if (!parts || parts.length < 3) return fallback;
  return {
    css: resolved,
    rgb: [
      Math.round(Number(parts[0])),
      Math.round(Number(parts[1])),
      Math.round(Number(parts[2])),
    ],
  };
}

function readPalette(theme: "dark" | "light"): ThemePalette {
  const fb = FALLBACK_PALETTES[theme];
  const probe = document.createElement("div");
  probe.style.opacity = "0";
  probe.style.pointerEvents = "none";
  probe.style.position = "absolute";
  document.body.appendChild(probe);

  const palette: ThemePalette = {
    accent: resolveColor(probe, "--chart-2", fb.accent),
    background: resolveColor(probe, "--background", fb.background),
    foreground: resolveColor(probe, "--foreground", fb.foreground),
    isDark: theme === "dark",
    mutedForeground: resolveColor(
      probe,
      "--muted-foreground",
      fb.mutedForeground
    ),
  };

  probe.remove();
  return palette;
}

function prefillLane(
  lane: LaneState,
  gpu: Gpu,
  streamW: number,
  laneH: number
) {
  const ratio = gpu.rps / MAX_RPS;
  const baseSpeed = (0.65 + ratio * 0.35) * (streamW / 1.8);
  const spawnRate = gpu.rps * 0.5;

  // Simulate ~3 seconds of dots to fill the lane
  const simTime = 3;
  const simStep = 1 / 60;
  for (let t = 0; t < simTime; t += simStep) {
    lane.accum += spawnRate * simStep;
    while (lane.accum >= 1) {
      lane.accum -= 1;
      lane.dots.push({
        alpha: 0.6 + Math.random() * 0.4,
        size: 1.8 + Math.random() * 2.2,
        speed: baseSpeed * (0.8 + Math.random() * 0.4),
        x: 0,
        yOff: (Math.random() - 0.5) * laneH * 0.45,
      });
    }
    for (const dot of lane.dots) {
      dot.x += dot.speed * simStep;
    }
    lane.dots = lane.dots.filter((d) => d.x <= streamW);
  }
}

function AnimatedCounter({
  value,
  delay,
}: {
  value: number;
  delay: number;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let frameId = 0;
    const duration = 1.8;
    const timeout = window.setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / (duration * 1000));
        const eased = 1 - (1 - p) ** 4;
        el.textContent = (value * eased).toFixed(1);
        if (p < 1) frameId = requestAnimationFrame(tick);
      };
      frameId = requestAnimationFrame(tick);
    }, delay * 1000);
    return () => {
      clearTimeout(timeout);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [value, delay]);

  return (
    <span ref={ref} className="font-mono text-lg font-bold tabular-nums text-foreground">
      0.0
    </span>
  );
}

function PerfAnimation() {
  const { resolvedTheme } = useTheme();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasWrapRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [palette, setPalette] = useState<ThemePalette>(
    FALLBACK_PALETTES.light
  );

  useEffect(() => {
    const theme =
      resolvedTheme === "light"
        ? "light"
        : resolvedTheme === "dark"
          ? "dark"
          : document.documentElement.classList.contains("dark")
            ? "dark"
            : "light";
    setPalette(readPalette(theme));
  }, [resolvedTheme]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const canvasWrap = canvasWrapRef.current;
    const container = containerRef.current;
    if (!canvas || !canvasWrap || !container) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const lanes: LaneState[] = GPUS.map(() => ({ accum: 0, dots: [] }));

    let width = 0;
    let height = 0;
    let laneH = 0;
    let streamW = 0;
    let prefilled = false;

    const resize = () => {
      width = canvasWrap.offsetWidth;
      height = canvasWrap.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      laneH = height / GPUS.length;
      streamW = width;
    };

    resize();
    window.addEventListener("resize", resize);

    // Pre-fill all lanes
    if (!prefilled) {
      for (let i = 0; i < GPUS.length; i++) {
        prefillLane(lanes[i], GPUS[i], streamW, laneH);
      }
      prefilled = true;
    }

    let prev = 0;
    let frameId = 0;

    const tick = (now: number) => {
      if (!prev) prev = now;
      const dt = Math.min((now - prev) / 1000, 0.05);
      prev = now;

      ctx.clearRect(0, 0, width, height);

      const [ar, ag, ab] = palette.accent.rgb;
      const [fr, fg, fb] = palette.foreground.rgb;

      for (let i = 0; i < GPUS.length; i++) {
        const gpu = GPUS[i];
        const lane = lanes[i];
        const cy = i * laneH + laneH / 2;
        const ratio = gpu.rps / MAX_RPS;

        // Spawn dots
        const baseSpeed = (0.65 + ratio * 0.35) * (streamW / 1.8);
        const spawnRate = gpu.rps * 0.5;
        lane.accum += spawnRate * dt;
        while (lane.accum >= 1) {
          lane.accum -= 1;
          lane.dots.push({
            alpha: 0.6 + Math.random() * 0.4,
            size: 1.8 + Math.random() * 2.2,
            speed: baseSpeed * (0.8 + Math.random() * 0.4),
            x: 0,
            yOff: (Math.random() - 0.5) * laneH * 0.45,
          });
        }

        // Update and draw dots
        let wi = 0;
        for (let j = 0; j < lane.dots.length; j++) {
          const dot = lane.dots[j];
          dot.x += dot.speed * dt;
          if (dot.x > streamW) continue;
          lane.dots[wi++] = dot;

          const dx = dot.x;
          const dy = cy + dot.yOff;

          // Fade at edges — start fading out at 80% of stream width
          const fadeIn = Math.min(1, dot.x / 40);
          const fadeZone = streamW * 0.2;
          const fadeOut = Math.min(1, (streamW - dot.x) / fadeZone);
          const fade = fadeIn * Math.max(0, fadeOut);

          // Trail (elongated glow behind the dot)
          const trailLen = dot.size * 8;
          const trailGrad = ctx.createLinearGradient(dx - trailLen, dy, dx, dy);
          trailGrad.addColorStop(0, `rgba(${ar}, ${ag}, ${ab}, 0)`);
          trailGrad.addColorStop(1, `rgba(${ar}, ${ag}, ${ab}, ${dot.alpha * fade * (palette.isDark ? 0.2 : 0.1)})`);
          ctx.fillStyle = trailGrad;
          ctx.beginPath();
          ctx.ellipse(dx - trailLen / 2, dy, trailLen / 2, dot.size * 1.2, 0, 0, Math.PI * 2);
          ctx.fill();

          // Soft glow
          ctx.globalAlpha = dot.alpha * fade * (palette.isDark ? 0.12 : 0.05);
          ctx.fillStyle = `rgb(${ar}, ${ag}, ${ab})`;
          ctx.beginPath();
          ctx.arc(dx, dy, dot.size * 4, 0, Math.PI * 2);
          ctx.fill();

          // Core dot
          ctx.globalAlpha = dot.alpha * fade * (palette.isDark ? 0.9 : 0.6);
          ctx.fillStyle = `rgb(${ar}, ${ag}, ${ab})`;
          ctx.beginPath();
          ctx.arc(dx, dy, dot.size, 0, Math.PI * 2);
          ctx.fill();

          // Hot center
          ctx.globalAlpha = dot.alpha * fade * (palette.isDark ? 0.7 : 0.4);
          ctx.fillStyle = `rgb(${fr}, ${fg}, ${fb})`;
          ctx.beginPath();
          ctx.arc(dx, dy, dot.size * 0.35, 0, Math.PI * 2);
          ctx.fill();
        }
        lane.dots.length = wi;
      }

      ctx.globalAlpha = 1;
      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
    };
  }, [palette]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* Main area */}
      <div
        ref={containerRef}
        style={{
          position: "relative",
          width: "100%",
          height: "clamp(20rem, 50vw, 28rem)",
        }}
      >
        {/* Canvas for particles (offset past GPU labels) */}
        <div
          ref={canvasWrapRef}
          style={{ position: "absolute", top: 0, bottom: 0, left: "clamp(5rem, 20vw, 9rem)", right: 0, zIndex: 1, overflow: "hidden" }}
        >
          <canvas ref={canvasRef} />
        </div>

        {/* DOM labels overlay */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            pointerEvents: "none",
          }}
        >
          {GPUS.map((gpu, i) => (
            <motion.div
              key={gpu.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                delay: 0.3 + i * STAGGER,
                duration: 0.7,
                ease: EASE,
              }}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingLeft: "clamp(1rem, 4vw, 2.5rem)",
                paddingRight: "clamp(1rem, 4vw, 2.5rem)",
              }}
            >
              <span className="text-sm font-semibold text-foreground sm:text-base">
                {gpu.name}
              </span>
              <div className="text-right">
                <AnimatedCounter
                  value={gpu.rps}
                  delay={0.5 + i * STAGGER}
                />
                <div className="font-mono text-[10px] text-muted-foreground/50">
                  req/s
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default PerfAnimation;
export { PerfAnimation };
